import { useState, useCallback, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Plus, MessageSquare } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { PageHeader } from '@/components/common/PageHeader'
import { SummaryStrip } from '@/components/common/SummaryStrip'
import type { SummaryPill } from '@/components/common/SummaryStrip'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { SessionCard } from '@/components/sessions/SessionCard'
import { SessionFilters } from '@/components/sessions/SessionFilters'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { EmptyState } from '@/components/common/EmptyState'
import { DataTablePagination } from '@/components/data-display/DataTablePagination'
import { useSessions, useDeleteSession, useCancelSession, useSessionStats } from '@/api/orchestrator'
import type { SessionsFilterParams, SessionStatus } from '@/lib/types'

const DEFAULT_PAGE_SIZE = 10

/**
 * Parse date from URL param string (YYYY-MM-DD format)
 */
function parseDateParam(value: string | null): Date | undefined {
  if (!value) return undefined
  const date = new Date(value)
  return isNaN(date.getTime()) ? undefined : date
}

/**
 * Format date for URL param (YYYY-MM-DD format)
 */
function formatDateParam(date: Date | undefined): string | undefined {
  if (!date) return undefined
  return date.toISOString().split('T')[0]
}

/**
 * Session list page for viewing and managing orchestration sessions.
 * Route: /sessions
 */
export function SessionListPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [deleteSessionId, setDeleteSessionId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  
  // Parse filters from URL
  const filters: SessionsFilterParams = useMemo(() => {
    const statusParam = searchParams.getAll('status')
    const validStatuses: SessionStatus[] = statusParam.filter((s): s is SessionStatus => 
      ['Idle', 'Running', 'WaitingForInput', 'Completed', 'Failed', 'Timeout', 'Queued', 'Paused', 'Resumable', 'Cancelled'].includes(s)
    )
    
    return {
      search: searchParams.get('search') || undefined,
      status: validStatuses.length > 0 ? validStatuses : undefined,
      dateFrom: parseDateParam(searchParams.get('dateFrom')),
      dateTo: parseDateParam(searchParams.get('dateTo')),
      projectId: searchParams.get('projectId') || undefined,
      includeExpired: searchParams.get('includeExpired') === 'true',
      excludeMode: 'Chatbot', // Always exclude chatbot sessions - they have their own page
      limit: Number(searchParams.get('limit')) || DEFAULT_PAGE_SIZE,
      offset: Number(searchParams.get('offset')) || 0
    }
  }, [searchParams])

  // Convenience variables
  const page = Math.floor((filters.offset ?? 0) / (filters.limit ?? DEFAULT_PAGE_SIZE)) + 1
  const pageSize = filters.limit ?? DEFAULT_PAGE_SIZE

  /**
   * Update URL params when filters change
   */
  const handleFiltersChange = useCallback((newFilters: SessionsFilterParams) => {
    const params = new URLSearchParams()
    
    if (newFilters.search) params.set('search', newFilters.search)
    if (newFilters.status && newFilters.status.length > 0) {
      newFilters.status.forEach(s => params.append('status', s))
    }
    if (newFilters.dateFrom) params.set('dateFrom', formatDateParam(newFilters.dateFrom)!)
    if (newFilters.dateTo) params.set('dateTo', formatDateParam(newFilters.dateTo)!)
    if (newFilters.projectId) params.set('projectId', newFilters.projectId)
    if (newFilters.includeExpired) params.set('includeExpired', 'true')
    if (newFilters.limit && newFilters.limit !== DEFAULT_PAGE_SIZE) {
      params.set('limit', String(newFilters.limit))
    }
    if (newFilters.offset && newFilters.offset > 0) {
      params.set('offset', String(newFilters.offset))
    }
    
    setSearchParams(params)
  }, [setSearchParams])

  // Fetch sessions with filters and pagination
  const {
    data: sessionsData,
    isLoading,
    error: fetchError,
    refetch,
  } = useSessions(filters)

  // Delete mutation
  const deleteSession = useDeleteSession()

  // Cancel mutation
  const cancelSession = useCancelSession()

  // Sessions are already filtered by the API (excludeMode: 'Chatbot')
  const sessions = sessionsData?.sessions ?? []
  const totalSessions = sessionsData?.total ?? 0

  const handleCancelSession = async (sessionId: string) => {
    try {
      await cancelSession.mutateAsync(sessionId)
      refetch()
    } catch (error) {
      console.error('Failed to cancel session:', error)
    }
  }

  const handleDeleteClick = (sessionId: string) => {
    setDeleteSessionId(sessionId)
    setDeleteError(null)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteSessionId) return

    try {
      await deleteSession.mutateAsync(deleteSessionId)
      setDeleteSessionId(null)
      refetch()
    } catch (error) {
      setDeleteError(
        error instanceof Error
          ? error.message
          : 'Failed to delete session'
      )
    }
  }

  const handleDeleteCancel = () => {
    setDeleteSessionId(null)
    setDeleteError(null)
  }

  const handlePageChange = useCallback((newPage: number) => {
    handleFiltersChange({
      ...filters,
      offset: (newPage - 1) * pageSize
    })
  }, [filters, handleFiltersChange, pageSize])

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    handleFiltersChange({
      ...filters,
      limit: newPageSize,
      offset: 0 // Reset to first page when page size changes
    })
  }, [filters, handleFiltersChange])

  // Fetch session stats for summary strip
  const { data: sessionStats, isLoading: statsLoading } = useSessionStats({ projectId: filters.projectId })

  // Build summary pills from stats
  const summaryPills = useMemo((): SummaryPill[] => {
    if (!sessionStats) return []
    const sc = sessionStats.statusCounts
    const active = (sc['Running'] || 0) + (sc['running'] || 0) + (sc['WaitingForInput'] || 0) + (sc['waitingForInput'] || 0)
    const queued = (sc['Queued'] || 0) + (sc['queued'] || 0)
    const failed = (sc['Failed'] || 0) + (sc['failed'] || 0)
    return [
      { label: 'Total', value: sessionStats.total, variant: 'default' as const },
      { label: 'Active', value: active, variant: 'primary' as const },
      { label: 'Queued', value: queued, variant: 'warning' as const },
      { label: 'Failed', value: failed, variant: 'error' as const },
    ]
  }, [sessionStats])

  return (
    <PageShell>
      <div className="p-6 space-y-4">
        <PageHeader
          title="Sessions"
          description="Manage your orchestration sessions"
          inlineContent={
            <SummaryStrip pills={summaryPills} isLoading={statsLoading} className="text-xs" />
          }
          actions={
            <Button asChild>
              <Link to="/orchestrate">
                <Plus className="h-4 w-4 mr-2" />
                New Task
              </Link>
            </Button>
          }
        />

        {/* Error Alerts */}
        {fetchError && (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Unable to load sessions. Please try again later.
            </AlertDescription>
          </Alert>
        )}
        {deleteError && (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{deleteError}</AlertDescription>
          </Alert>
        )}

        {/* Filters — full-width, no card wrapper for cleaner look */}
        <SessionFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
        />

        {/* Loading State */}
        {isLoading && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 text-muted-foreground">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
                Loading sessions...
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!isLoading && !fetchError && sessions.length === 0 && (
          <EmptyState
            icon={MessageSquare}
            title="No sessions found"
            description="Get started by creating a new orchestration task, or adjust your filters."
            action={
              <Button asChild>
                <Link to="/orchestrate">
                  <Plus className="h-4 w-4 mr-2" />
                  Start New Task
                </Link>
              </Button>
            }
          />
        )}

        {/* Session List */}
        {!isLoading && !fetchError && sessions.length > 0 && (
          <>
            <p className="text-sm text-muted-foreground">{totalSessions} session{totalSessions !== 1 ? 's' : ''}</p>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
              {sessions.map((session) => (
                <SessionCard
                  key={session.sessionId}
                  session={session}
                  onCancel={handleCancelSession}
                  onDelete={() => handleDeleteClick(session.sessionId)}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalSessions > 0 && (
              <DataTablePagination
                page={page}
                pageSize={pageSize}
                total={totalSessions}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[10, 25, 50]}
              />
            )}
          </>
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteSessionId !== null}
          onOpenChange={(open) => {
            if (!open) handleDeleteCancel()
          }}
          title="Delete Session"
          description="Are you sure you want to delete this session? This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={handleDeleteConfirm}
          variant="destructive"
        />
      </div>
    </PageShell>
  )
}
